import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import { SubmitFilingResponse } from '@/types/filings';

interface FilingTransmissionModalProps {
  filingDocumentId: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: (transmissionId: number) => void;
}

export const FilingTransmissionModal: React.FC<FilingTransmissionModalProps> = ({
  filingDocumentId,
  isOpen,
  onClose,
  onSubmitSuccess
}) => {
  const [isTest, setIsTest] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await ApiService.request<SubmitFilingResponse>(
        EndpointKeys.SUBMIT_FILING_MANUALLY,
        {
          method: 'POST',
          body: {
            filing_document_id: filingDocumentId,
            is_test: isTest
          }
        }
      );

      if (response.success && response.transmission_id) {
        onSubmitSuccess(response.transmission_id);
        onClose();
      } else {
        setError(response.error || 'Submission failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit filing');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Submit Filing to HMRC</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <Alert>
            <AlertDescription>
              This will submit the filing directly to HMRC. 
              {isTest ? ' This is a TEST submission.' : ' This is a PRODUCTION submission.'}
            </AlertDescription>
          </Alert>

          <div className="flex items-center space-x-2">
            <Switch
              id="test-mode"
              checked={isTest}
              onCheckedChange={setIsTest}
              data-testid="test-mode-switch"
            />
            <Label htmlFor="test-mode">
              Test Submission {isTest ? '(HMRC-PAYE-RTI-FPS-TIL)' : '(HMRC-PAYE-RTI-FPS)'}
            </Label>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              data-testid="submit-filing-button"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Filing'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 